import type { Nullable } from "models-webgl2";
import { Vec2 } from "utils/math/vec2";
import { Webgl2dRenderer } from "../../../webgl2d-renderer";
import { Webgl2 } from "../../../webgl2";
import { Webgl2Component } from "../../webgl2-component";
import { CanvasResizeSelectionBottomComponent } from "./canvas-resize-selection-bottom-component";
import { CanvasResizeSelectionBottomLeftComponent } from "./canvas-resize-selection-bottom-left-component";
import { CanvasResizeSelectionBottomRightComponent } from "./canvas-resize-selection-bottom-right-component";
import { CanvasResizeSelectionLeftComponent } from "./canvas-resize-selection-left-component";
import { CanvasResizeSelectionRightComponent } from "./canvas-resize-selection-right-component";
import { CanvasResizeSelectionTopComponent } from "./canvas-resize-selection-top-component";
import { CanvasResizeSelectionTopLeftComponent } from "./canvas-resize-selection-top-left-component";
import { CanvasResizeSelectionTopRightComponent } from "./canvas-resize-selection-top-right-component";

export class CanvasResizeSelectionComponent extends Webgl2Component {
  private _prevTranslation = Vec2.zero();
  private _mouseDownPosition = Vec2.zero();

  constructor(private readonly _renderer: Webgl2dRenderer) {
    super();

    const topLeftComponent = new CanvasResizeSelectionTopLeftComponent(this._renderer, this);
    topLeftComponent.style.top = "0";
    topLeftComponent.style.left = "0";
    topLeftComponent.style.translateX = "-2px";
    topLeftComponent.style.translateY = "-2px";
    this.appendChild(topLeftComponent);

    const topComponent = new CanvasResizeSelectionTopComponent(this._renderer, this);
    topComponent.style.top = "0";
    topComponent.style.left = "50%";
    topComponent.style.translateX = "-50%";
    topComponent.style.translateY = "-50%";
    this.appendChild(topComponent);

    const topRightComponent = new CanvasResizeSelectionTopRightComponent(this._renderer, this);
    topRightComponent.style.top = "0";
    topRightComponent.style.left = "100%";
    topRightComponent.style.translateX = "-100%&2px";
    topRightComponent.style.translateY = "-2px";
    this.appendChild(topRightComponent);

    const leftComponent = new CanvasResizeSelectionLeftComponent(this._renderer, this);
    leftComponent.style.top = "50%";
    leftComponent.style.left = "0";
    leftComponent.style.translateX = "-50%";
    leftComponent.style.translateY = "-50%";
    this.appendChild(leftComponent);

    const rightComponent = new CanvasResizeSelectionRightComponent(this._renderer, this);
    rightComponent.style.top = "50%";
    rightComponent.style.left = "100%";
    rightComponent.style.translateX = "-50%";
    rightComponent.style.translateY = "-50%";
    this.appendChild(rightComponent);

    const bottomLeftComponent = new CanvasResizeSelectionBottomLeftComponent(this._renderer, this);
    bottomLeftComponent.style.top = "100%";
    bottomLeftComponent.style.left = "0";
    bottomLeftComponent.style.translateX = "-2px";
    bottomLeftComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomLeftComponent);

    const bottomComponent = new CanvasResizeSelectionBottomComponent(this._renderer, this);
    bottomComponent.style.top = "100%";
    bottomComponent.style.left = "50%";
    bottomComponent.style.translateX = "-50%";
    bottomComponent.style.translateY = "-50%";
    this.appendChild(bottomComponent);

    const bottomRightComponent = new CanvasResizeSelectionBottomRightComponent(
      this._renderer,
      this,
    );
    bottomRightComponent.style.top = "100%";
    bottomRightComponent.style.left = "100%";
    bottomRightComponent.style.translateX = "-100%&2px";
    bottomRightComponent.style.translateY = "-100%&2px";
    this.appendChild(bottomRightComponent);

    // TODO:?
    // NOTE: This is crutch to update component on scale change, since component doesn't have
    // relation to camera change
    this._renderer.camera.events.on("scale", (newScale: number) => {
      const value = 1 / newScale;
      this.forEachChildren((child) => {
        child.style.scaleX = value * window.devicePixelRatio;
        child.style.scaleY = value * window.devicePixelRatio;
      });
    });
  }

  public copyComponentTransform(component: Nullable<Webgl2Component<any, any>>) {
    if (!component) return;
    this.transform.copy(component.transform);
  }

  public onMouseDown(e: MouseEvent | TouchEvent) {
    this._mouseDownPosition = this._renderer.camera.getGlobalMousePixelPosition(e);
    this._prevTranslation = this._renderer.canvasTranslation.clone();
  }

  public onMouseMove(e: MouseEvent | TouchEvent) {
    const currentMousePos = this._renderer.camera.getGlobalMousePixelPosition(e);
    const diff = currentMousePos.sub(this._mouseDownPosition);
    const newTranslation = diff.add(this._prevTranslation);

    this.transform.setTranslation(newTranslation);
    this._renderer.setCanvasTranslate(newTranslation);
  }

  public getRenderData() {
    if (!this._renderer.hasSelectedComponent) return;

    const material = Webgl2.materials.selectionGrid;
    material.setUniforms({
      u_size: this.transform.size.getAbs().getArray(),
      u_showGrid: true,
    });
    return { material };
  }
}

export default CanvasResizeSelectionComponent;
